import { useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { RoutineRunView } from "../utils/hosted-agent-routine-runs"
import type { HostedAgentRoutineFormState } from "../utils/hosted-agent-routines"
import type { HostedAgentRoutineRunWarning } from "@/api/admin/hosted-agents-routines"
import type { BadgeProps } from "@/components/ui/badge"
import type { TFunction } from "i18next"

import { AlertTriangle, Play } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Spinner } from "@/components/ui/spinner"

import {
  useAdminHostedAgentRoutineRunsQuery,
  useRunHostedAgentRoutineNowMutation,
} from "../hooks/admin-queries"
import {
  isRoutineRunInFlight,
  ROUTINE_RUN_HISTORY_POLL_MS,
  routineRunWarnings,
  toRoutineRunView,
} from "../utils/hosted-agent-routine-runs"
import { HOSTED_AGENT_RUN_STATUS_DEFAULTS } from "../utils/hosted-agent-routines"
import { formatHostedAgentTimestamp } from "../utils/hosted-agents"

/**
 * A run warning in the UI language when its code is known (e.g.
 * `routine_owner_groups_unresolved`), else the backend's message, else the
 * bare code.
 */
function routineRunWarningText(t: TFunction, warning: HostedAgentRoutineRunWarning): string {
  const fallback = warning.message || warning.code
  if (!/^[a-z_]+$/.test(warning.code)) return fallback
  return t(`adminDashboard.hostedAgents.routinesTab.runWarnings.${warning.code}`, {
    defaultValue: fallback,
  })
}

// Run-now control + recent run history for one routine (B1). A routine that
// has not been saved yet has no server id to run, so the control is disabled
// until the agent is saved (`createdAt` present).
export function HostedAgentRoutineRunHistory({
  agentId,
  routine,
}: {
  agentId: string | undefined
  routine: HostedAgentRoutineFormState
}) {
  const { t } = useTranslation()
  const runsQuery = useAdminHostedAgentRoutineRunsQuery(agentId, routine.id)
  const runNow = useRunHostedAgentRoutineNowMutation()
  const [runError, setRunError] = useState<string | null>(null)
  const isPersisted = Boolean(routine.createdAt)
  const runs = useMemo(() => (runsQuery.data ?? []).map(toRoutineRunView), [runsQuery.data])
  const inFlight = runs.some(isRoutineRunInFlight)
  const { refetch } = runsQuery
  // A queued/running run settles on its own; re-read until it does instead of
  // leaving "Queued" on screen until the dialog is closed and reopened.
  useEffect(() => {
    if (!inFlight) return
    const timer = setInterval(() => void refetch(), ROUTINE_RUN_HISTORY_POLL_MS)
    return () => clearInterval(timer)
  }, [inFlight, refetch])

  const handleRunNow = async () => {
    if (!agentId) return
    setRunError(null)
    try {
      const result = await runNow.mutateAsync({ agentId, routineId: routine.id })
      // The run was accepted (202); a warning means it will execute degraded
      // (e.g. without the owner's groups), which the admin must see now, not
      // only after opening the history.
      const warnings = routineRunWarnings(result.warnings)
      if (warnings.length === 0) {
        toast.success(
          t("adminDashboard.hostedAgents.routinesTab.runStarted", { defaultValue: "Run started." })
        )
      } else {
        // Degraded, not failed: the warning variant, never the green success one.
        toast.warning(
          t("adminDashboard.hostedAgents.routinesTab.runStartedWithWarnings", {
            defaultValue: "Run started with warnings.",
          }),
          { description: warnings.map((warning) => routineRunWarningText(t, warning)).join(" ") }
        )
      }
    } catch (error) {
      setRunError(
        error instanceof Error
          ? error.message
          : t("adminDashboard.hostedAgents.routinesTab.runError", {
              defaultValue: "Could not start the run.",
            })
      )
    }
  }

  return (
    <div className="space-y-3 border-t pt-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium">
          {t("adminDashboard.hostedAgents.routinesTab.historyTitle", {
            defaultValue: "Run history",
          })}
        </span>
        <Button
          disabled={runNow.isPending || !agentId || !isPersisted}
          size="sm"
          type="button"
          variant="outline"
          onClick={() => void handleRunNow()}
        >
          {runNow.isPending ? <Spinner className="size-4" /> : <Play className="size-4" />}
          {t("adminDashboard.hostedAgents.routinesTab.runNow", { defaultValue: "Run now" })}
        </Button>
      </div>

      {!isPersisted ? (
        <p className="text-muted-foreground text-xs">
          {t("adminDashboard.hostedAgents.routinesTab.saveFirst", {
            defaultValue: "Save the agent to enable running this routine and viewing its history.",
          })}
        </p>
      ) : (
        <RoutineRunList
          error={runError}
          isError={runsQuery.isError}
          isLoading={runsQuery.isLoading}
          runs={runs}
        />
      )}
    </div>
  )
}

function RoutineRunList({
  isLoading,
  isError,
  error,
  runs,
}: {
  isLoading: boolean
  isError: boolean
  error: string | null
  runs: RoutineRunView[]
}) {
  const { t, i18n } = useTranslation()

  if (error) {
    return (
      <p className="bg-destructive text-destructive-foreground border-foreground rounded-md border px-2 py-1 text-xs">
        {error}
      </p>
    )
  }
  if (isLoading) {
    return (
      <p className="text-muted-foreground flex items-center gap-2 text-xs">
        <Spinner className="size-3" />
        {t("adminDashboard.hostedAgents.routinesTab.historyLoading", {
          defaultValue: "Loading runs…",
        })}
      </p>
    )
  }
  if (isError) {
    return (
      <p className="text-muted-foreground text-xs">
        {t("adminDashboard.hostedAgents.routinesTab.historyUnavailable", {
          defaultValue: "Run history is unavailable right now.",
        })}
      </p>
    )
  }
  if (runs.length === 0) {
    return (
      <p className="text-muted-foreground text-xs">
        {t("adminDashboard.hostedAgents.routinesTab.historyEmpty", {
          defaultValue: "No runs yet.",
        })}
      </p>
    )
  }
  return (
    <ul className="space-y-1.5">
      {runs.map((run) => (
        <RoutineRunRow key={run.runId} locale={i18n.language} run={run} />
      ))}
    </ul>
  )
}

function RoutineRunRow({ run, locale }: { run: RoutineRunView; locale: string }) {
  const { t } = useTranslation()
  const started = formatHostedAgentTimestamp(run.startedAt, locale)
  const duration =
    run.durationMs !== null
      ? t("adminDashboard.hostedAgents.routinesTab.durationSeconds", {
          defaultValue: "{{seconds}} s",
          seconds: (run.durationMs / 1000).toFixed(1),
        })
      : null

  return (
    <li className="flex flex-wrap items-center gap-2 text-xs">
      <RunStatusBadge rawStatus={run.rawStatus} status={run.status} />
      <span className="text-muted-foreground">
        {started ??
          t("adminDashboard.hostedAgents.routinesTab.runPending", { defaultValue: "Pending" })}
      </span>
      {duration ? <span className="text-muted-foreground">· {duration}</span> : null}
      {run.errorCode ? (
        <span className="bg-destructive text-destructive-foreground border-foreground rounded-md border px-2 py-1 font-mono">
          · {run.errorCode}
        </span>
      ) : null}
      {run.warnings.map((warning, index) => (
        <RoutineRunWarningLine key={`${warning.code}-${index}`} warning={warning} />
      ))}
    </li>
  )
}

function RoutineRunWarningLine({ warning }: { warning: HostedAgentRoutineRunWarning }) {
  const { t } = useTranslation()
  const text = routineRunWarningText(t, warning)
  return (
    <span
      className="flex basis-full items-start gap-2"
      data-testid="routine-run-warning"
      title={warning.code || undefined}
    >
      <Badge className="shrink-0 gap-1" variant="warning">
        <AlertTriangle aria-hidden="true" className="size-3" />
        {t("adminDashboard.hostedAgents.routinesTab.runWarningLabel", { defaultValue: "Warning" })}
      </Badge>
      <span className="text-muted-foreground">{text}</span>
    </span>
  )
}

function runStatusVariant(status: string): BadgeProps["variant"] {
  if (status === "succeeded") return "success"
  if (status === "failed" || status === "cancelled") return "destructive"
  return "secondary"
}

function RunStatusBadge({
  status,
  rawStatus,
}: {
  status: RoutineRunView["status"]
  rawStatus: string
}) {
  const { t } = useTranslation()
  // An unmapped ledger value shows verbatim rather than as a wrong badge.
  const label =
    status === "unknown"
      ? rawStatus
      : t(`adminDashboard.hostedAgents.routinesTab.status.${status}`, {
          defaultValue: HOSTED_AGENT_RUN_STATUS_DEFAULTS[status] ?? status,
        })
  return <Badge variant={runStatusVariant(status)}>{label}</Badge>
}
